import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { usePlaidLink } from 'react-plaid-link';
import { toast } from 'sonner';
import { unifiedAPI } from '../services/api';
import {
  ArrowLeft, Plus, Bank, ArrowsClockwise, Camera, Receipt as ReceiptIcon,
  Warning, X, LinkSimple
} from '@phosphor-icons/react';

const GREEN = '#62CF5A';
const AMBER = '#F2B33D';
const BEBAS = { fontFamily: "'Bebas Neue', sans-serif" };
const card = 'bg-[#141412] border border-[#2A2A26] rounded-[10px]';
const fmt = (n) => (n == null ? '—' : `${n < 0 ? '-' : ''}$${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

/* Converging streams header visual */
function StreamVisual({ accounts }) {
  const n = Math.max(accounts.length, 2);
  const paths = Array.from({ length: n }, (_, i) => {
    const y0 = 10 + (i * 60) / Math.max(n - 1, 1);
    return `M0,${y0} C120,${y0} 220,40 400,40`;
  });
  return (
    <svg viewBox="0 0 400 80" className="w-full h-20" preserveAspectRatio="none" aria-hidden="true">
      {paths.map((p, i) => (
        <path key={`stream-${i}`} d={p} fill="none" strokeWidth="1.5"
          stroke={accounts[i]?.account_subtype === 'personal' ? AMBER : GREEN}
          strokeOpacity="0.7" strokeDasharray="240" strokeDashoffset="240">
          <animate attributeName="stroke-dashoffset" from="240" to="0" dur="1.6s" begin={`${i * 0.15}s`} fill="freeze" />
        </path>
      ))}
      <path d="M400,40 L400,40" stroke={GREEN} />
    </svg>
  );
}

function CategoryChip({ cat }) {
  const colors = { Revenue: GREEN, COGS: '#8B5CF6', Opex: '#3B82F6', Payroll: '#FBBF24', Excluded: '#666', Untagged: '#EF4444' };
  const c = colors[cat] || '#999';
  return <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold" style={{ background: `${c}1c`, color: c }}>{cat}</span>;
}

const CATS = ['Revenue', 'COGS', 'Opex', 'Payroll', 'Excluded', 'Untagged'];

export default function UnifiedDashboardPage() {
  const [data, setData] = useState(null);
  const [linkToken, setLinkToken] = useState(null);
  const [classifyQueue, setClassifyQueue] = useState([]);
  const [receiptDraft, setReceiptDraft] = useState(null);
  const [cashMode, setCashMode] = useState(false);
  const [busy, setBusy] = useState(false);
  const [plaidReady, setPlaidReady] = useState(true);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await unifiedAPI.overview();
      setData(d);
      setClassifyQueue(d.accounts.filter((a) => !a.account_subtype));
    } catch { toast.error('Failed to load dashboard'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const getLinkToken = async () => {
    try {
      const { data: d } = await unifiedAPI.linkToken();
      setLinkToken(d.link_token);
    } catch (e) {
      setPlaidReady(false);
      toast.error(e?.response?.data?.detail || 'Plaid not configured yet');
    }
  };

  const onSuccess = useCallback(async (publicToken, metadata) => {
    try {
      await unifiedAPI.exchange(publicToken, metadata?.institution?.name);
      toast.success(`${metadata?.institution?.name || 'Bank'} linked`);
      setLinkToken(null);
      setTimeout(load, 2500);
    } catch { toast.error('Linking failed'); }
  }, [load]);

  const { open, ready } = usePlaidLink({ token: linkToken, onSuccess });
  useEffect(() => { if (linkToken && ready) open(); }, [linkToken, ready, open]);

  const classify = async (accountId, subtype) => {
    await unifiedAPI.classify(accountId, subtype);
    setClassifyQueue((q) => q.filter((a) => a.account_id !== accountId));
    load();
  };

  const fixCategory = async (txId, cat) => {
    try {
      const { data: d } = await unifiedAPI.fixCategory(txId, cat);
      toast.success(d.remembered_merchant ? `Saved — "${d.remembered_merchant}" will auto-tag as ${cat}` : 'Updated');
      load();
    } catch { toast.error('Update failed'); }
  };

  const onReceiptFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const b64 = await new Promise((res) => {
        const r = new FileReader();
        r.onload = () => res(r.result.split(',')[1]);
        r.readAsDataURL(file);
      });
      const { data: d } = await unifiedAPI.receiptOcr(b64);
      setReceiptDraft({ ...d, category: 'COGS' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not read receipt');
    } finally { setBusy(false); e.target.value = ''; }
  };

  const confirmReceipt = async () => {
    setBusy(true);
    try {
      const { data: d } = await unifiedAPI.receiptConfirm(receiptDraft);
      toast.success(d.result === 'attached' ? 'Receipt attached to a matching bank transaction' : 'Cash expense logged to P&L');
      setReceiptDraft(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  const logCash = () => {
    setCashMode(true);
    setReceiptDraft({ receipt_id: null, vendor: '', amount: '', date: new Date().toISOString().slice(0, 10), category: 'COGS', manual: true });
  };

  const confirmCash = async () => {
    setBusy(true);
    try {
      // manual cash entry: reuse confirm with a synthetic OCR-less receipt
      const { data: d } = await unifiedAPI.receiptOcrManual(receiptDraft);
      toast.success(d.result === 'attached' ? 'Matched an existing transaction' : 'Cash expense logged');
      setReceiptDraft(null); setCashMode(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center" style={{ fontFamily: "'Barlow', sans-serif" }}>Loading…</div>;

  const { accounts, pnl, recent_activity, weekly_chart, financing } = data;
  const reauth = data.items?.some((i) => i.status === 'reauth_required');
  const maxV = Math.max(...weekly_chart.map((w) => Math.max(w.income, w.expense)), 1);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white pb-16" style={{ fontFamily: "'Barlow', sans-serif" }} data-testid="unified-dashboard">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center justify-between mb-4">
          <Link to="/dashboard" className="text-[#999] hover:text-white flex items-center gap-2 text-sm"><ArrowLeft className="w-4 h-4" /> Dashboard</Link>
          <div className="flex gap-2">
            <button onClick={() => unifiedAPI.syncAll().then(() => { toast.success('Sync started'); setTimeout(load, 3000); })}
              className={`${card} px-3 h-9 text-xs flex items-center gap-1.5 hover:border-[#62CF5A]`} data-testid="sync-button">
              <ArrowsClockwise className="w-3.5 h-3.5" /> Refresh
            </button>
            <button onClick={getLinkToken} disabled={!plaidReady}
              className="h-9 px-4 rounded-[8px] font-bold text-black text-xs flex items-center gap-1.5 disabled:opacity-50" style={{ background: GREEN }} data-testid="link-account-button">
              <Plus className="w-3.5 h-3.5" weight="bold" /> Link account
            </button>
          </div>
        </div>

        {reauth && (
          <div className={`${card} px-4 py-2.5 mb-4 flex items-center gap-2 text-xs text-[#F2B33D]`} data-testid="reauth-banner">
            <Warning className="w-4 h-4" /> A bank connection needs re-authentication — balances shown as of last sync.
          </div>
        )}

        {/* Header: stream visual + account chips */}
        <div className={`${card} p-5 mb-5`} data-testid="stream-header">
          <p className="text-xs uppercase tracking-[0.2em] text-[#777] mb-1">Everything in one place</p>
          <StreamVisual accounts={accounts} />
          <div className="flex flex-wrap gap-2 mt-2">
            {accounts.length === 0 && <span className="text-xs text-[#666]">No accounts linked yet — link your first bank to begin.</span>}
            {accounts.map((a) => (
              <span key={a.account_id} className="text-[11px] px-2.5 py-1 rounded-full border font-medium"
                style={a.account_subtype === 'personal'
                  ? { borderColor: `${AMBER}55`, color: AMBER, background: `${AMBER}12` }
                  : { borderColor: `${GREEN}55`, color: GREEN, background: `${GREEN}10` }}
                data-testid={`account-chip-${a.account_id}`}>
                {a.name} {a.account_subtype === 'personal' && '· flagged for business use'}
              </span>
            ))}
          </div>
        </div>

        {/* Classification prompts */}
        {classifyQueue.map((a) => (
          <div key={a.account_id} className={`${card} p-4 mb-4 flex flex-col sm:flex-row sm:items-center gap-3`} data-testid={`classify-${a.account_id}`}>
            <p className="text-sm flex-1"><span className="font-bold">{a.name}</span> — is this a business account, or a personal account you also use for business?</p>
            <div className="flex gap-2">
              <button onClick={() => classify(a.account_id, 'business')} className="h-9 px-4 rounded-[8px] text-xs font-bold text-black" style={{ background: GREEN }} data-testid={`classify-business-${a.account_id}`}>Business</button>
              <button onClick={() => classify(a.account_id, 'personal')} className="h-9 px-4 rounded-[8px] text-xs font-bold text-black" style={{ background: AMBER }} data-testid={`classify-personal-${a.account_id}`}>Personal (used for business)</button>
            </div>
          </div>
        ))}

        {/* Row 1: summary cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
          <div className={`${card} p-5`} data-testid="card-cash-position">
            <p className="text-xs text-[#999] mb-1">Unified Cash Position</p>
            <p className="text-4xl" style={{ ...BEBAS, color: GREEN }}>{fmt(data.cash_position)}</p>
          </div>
          <div className={`${card} p-5`} data-testid="card-net-pnl">
            <p className="text-xs text-[#999] mb-1">This Month Net P&L</p>
            <p className="text-4xl" style={{ ...BEBAS, color: pnl.net_income >= 0 ? GREEN : '#EF4444' }}>{fmt(pnl.net_income)}</p>
            <p className="text-[10px] text-[#666] mt-1">Rev {fmt(pnl.revenue)} · COGS {fmt(pnl.cogs)} · Opex {fmt(pnl.opex)} · Payroll {fmt(pnl.payroll)}</p>
          </div>
          <div className={`${card} p-5`} data-testid="card-untagged">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-[#999] mb-1">Untagged Spend</p>
                <p className="text-4xl" style={{ ...BEBAS, color: pnl.untagged_count ? '#EF4444' : '#666' }}>{fmt(pnl.untagged_amount)}</p>
                <p className="text-[10px] text-[#666] mt-1">{pnl.untagged_count} transaction{pnl.untagged_count === 1 ? '' : 's'} need tagging</p>
              </div>
              <button onClick={logCash} className="text-[10px] px-2.5 py-1.5 rounded-[8px] border border-[#2A2A26] hover:border-[#62CF5A] flex items-center gap-1" data-testid="log-cash-button">
                <ReceiptIcon className="w-3 h-3" /> Log a cash expense
              </button>
            </div>
          </div>
        </div>

        {/* Row 2: chart + financing */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
          <div className={`${card} p-5`} data-testid="income-expense-chart">
            <p className="text-xs text-[#999] mb-4">Income vs Expense — last 6 weeks</p>
            {weekly_chart.length === 0 ? <p className="text-xs text-[#555] py-8 text-center">No synced transactions yet.</p> : (
              <div className="flex items-end gap-3 h-36">
                {weekly_chart.map((w) => (
                  <div key={w.week} className="flex-1 flex items-end gap-1">
                    <div className="flex-1 rounded-t-sm" style={{ height: `${(w.income / maxV) * 100}%`, background: GREEN, minHeight: 2 }} title={`Income ${fmt(w.income)}`} />
                    <div className="flex-1 rounded-t-sm" style={{ height: `${(w.expense / maxV) * 100}%`, background: '#3B82F6', minHeight: 2 }} title={`Expense ${fmt(w.expense)}`} />
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-4 mt-3 text-[10px] text-[#999]">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm" style={{ background: GREEN }} /> Income</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[#3B82F6]" /> Expense</span>
            </div>
          </div>

          <div className={`${card} p-5 flex flex-col`} data-testid="financing-card">
            {financing.provider === 'stripe' ? (
              <>
                <p className="text-xs uppercase tracking-[0.15em] mb-2" style={{ color: GREEN }}>Pre-approved financing</p>
                <pre className="text-xs text-[#ccc] bg-[#1C1C19] border border-[#2A2A26] rounded-[8px] p-3 overflow-auto flex-1">{JSON.stringify(financing.terms, null, 2)}</pre>
                <button className="mt-3 h-11 rounded-[8px] font-bold text-black text-sm" style={{ background: GREEN }} data-testid="financing-accept">Review & accept in Stripe</button>
              </>
            ) : (
              <>
                <p className="text-xs uppercase tracking-[0.15em] text-[#999] mb-2">Financing</p>
                <h3 className="text-2xl mb-2" style={BEBAS}>See if you qualify for funding</h3>
                <p className="text-xs text-[#999] leading-relaxed flex-1">Based on your revenue activity, you may qualify for financing through our partner REIL Capital. Divispend performs no underwriting or credit decisioning.</p>
                {financing.configured ? (
                  <a href={financing.apply_url} target="_blank" rel="noreferrer"
                    onClick={() => unifiedAPI.reilTrack('submitted')}
                    className="mt-3 h-11 rounded-[8px] font-bold text-black text-sm flex items-center justify-center gap-2" style={{ background: GREEN }} data-testid="financing-reil-apply">
                    <LinkSimple className="w-4 h-4" /> Start application
                  </a>
                ) : (
                  <p className="mt-3 text-[10px] text-[#666] border border-[#2A2A26] rounded-[8px] px-3 py-2">Referral link pending partner setup.</p>
                )}
                {financing.status !== 'none' && <p className="text-[10px] text-[#999] mt-2">Referral status: <span className="text-white">{financing.status}</span>{financing.commission_earned ? ` · commission ${fmt(financing.commission_earned)}` : ''}</p>}
              </>
            )}
            <p className="text-[10px] text-[#666] mt-3" data-testid="financing-disclaimer">{financing.disclaimer}</p>
          </div>
        </div>

        {/* Row 3: linked accounts */}
        <div className={`${card} mb-5`} data-testid="linked-accounts">
          <p className="text-xs text-[#999] px-5 pt-4 pb-2">Linked Accounts</p>
          {accounts.length === 0 ? <p className="text-xs text-[#555] px-5 pb-5">None yet.</p> : accounts.map((a) => (
            <div key={a.account_id} className="px-5 py-3 border-t border-[#2A2A26] flex items-center gap-3" data-testid={`account-row-${a.account_id}`}>
              <Bank className="w-5 h-5 shrink-0" style={{ color: a.account_subtype === 'personal' ? AMBER : GREEN }} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate">{a.name} <span className="text-[#666] font-normal">·· {a.mask}</span></p>
                <p className="text-[10px] text-[#666]">{a.institution_name}{a.item_status === 'reauth_required' && ` · as of ${a.last_synced_at ? new Date(a.last_synced_at).toLocaleDateString() : 'last sync'}`}</p>
              </div>
              {a.account_subtype && (
                <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
                  style={a.account_subtype === 'personal' ? { background: `${AMBER}1c`, color: AMBER } : { background: `${GREEN}18`, color: GREEN }}>
                  {a.account_subtype === 'personal' ? 'personal — flagged for business use' : 'business'}
                </span>
              )}
              <p className="text-base" style={BEBAS}>{fmt(a.current_balance)}</p>
              <button onClick={() => unifiedAPI.removeItem(a.item_id).then(() => { toast.success('Disconnected at Plaid'); load(); })}
                className="text-[#555] hover:text-[#EF4444]" title="Disconnect" data-testid={`disconnect-${a.account_id}`}><X className="w-4 h-4" /></button>
            </div>
          ))}
        </div>

        {/* Row 4: recent activity */}
        <div className={`${card}`} data-testid="recent-activity">
          <div className="flex items-center justify-between px-5 pt-4 pb-2">
            <p className="text-xs text-[#999]">Recent Activity</p>
            <button onClick={() => fileRef.current?.click()} disabled={busy}
              className="text-[10px] px-2.5 py-1.5 rounded-[8px] border border-[#2A2A26] hover:border-[#62CF5A] flex items-center gap-1" data-testid="add-receipt-global">
              <Camera className="w-3 h-3" /> {busy ? 'Reading…' : 'Add receipt'}
            </button>
          </div>
          {recent_activity.length === 0 ? <p className="text-xs text-[#555] px-5 pb-5">No activity yet — link an account or log a cash expense.</p> :
            recent_activity.map((t) => {
              const acct = accounts.find((a) => a.account_id === t.account_id);
              return (
                <div key={t.transaction_id} className="px-5 py-2.5 border-t border-[#2A2A26] flex items-center gap-3 text-sm" data-testid={`tx-${t.transaction_id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="truncate">{t.merchant_name || t.name}{t.receipt_id && <ReceiptIcon className="w-3 h-3 inline ml-1.5 text-[#62CF5A]" />}</p>
                    <p className="text-[10px] text-[#666]">{t.date} · {t.source === 'receipt' ? 'Logged receipt' : (acct ? `${acct.name}${acct.account_subtype === 'personal' ? ' (personal ⚑)' : ''}` : 'Bank')}</p>
                  </div>
                  <select value={t.category} onChange={(e) => fixCategory(t.transaction_id, e.target.value)}
                    className="bg-[#1C1C19] border border-[#2A2A26] rounded-[8px] text-[10px] px-1.5 py-1 text-white" data-testid={`cat-select-${t.transaction_id}`}>
                    {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <CategoryChip cat={t.category} />
                  <p className="w-24 text-right" style={{ ...BEBAS, color: t.amount < 0 ? GREEN : '#fff' }}>{t.amount < 0 ? '+' : '-'}{fmt(Math.abs(t.amount)).replace('-', '')}</p>
                </div>
              );
            })}
        </div>

        <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onReceiptFile} data-testid="receipt-file-input" />

        {/* Receipt confirm modal */}
        {receiptDraft && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => { setReceiptDraft(null); setCashMode(false); }}>
            <div className={`${card} p-5 w-full max-w-sm`} onClick={(e) => e.stopPropagation()} data-testid="receipt-confirm-modal">
              <h3 className="text-xl mb-3" style={BEBAS}>{cashMode ? 'Log a cash expense' : 'Confirm receipt'}</h3>
              {['vendor', 'amount', 'date'].map((f) => (
                <input key={f} type={f === 'amount' ? 'number' : f === 'date' ? 'date' : 'text'}
                  value={receiptDraft[f] ?? ''} placeholder={f[0].toUpperCase() + f.slice(1)}
                  onChange={(e) => setReceiptDraft((r) => ({ ...r, [f]: e.target.value }))}
                  className="w-full bg-[#1C1C19] border border-[#2A2A26] rounded-[8px] px-3 h-10 text-sm mb-2.5 focus:outline-none focus:border-[#62CF5A]"
                  data-testid={`receipt-${f}`} />
              ))}
              <select value={receiptDraft.category} onChange={(e) => setReceiptDraft((r) => ({ ...r, category: e.target.value }))}
                className="w-full bg-[#1C1C19] border border-[#2A2A26] rounded-[8px] px-3 h-10 text-sm mb-4" data-testid="receipt-category">
                {CATS.filter((c) => c !== 'Excluded' && c !== 'Untagged').map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <button onClick={cashMode ? confirmCash : confirmReceipt} disabled={busy || !receiptDraft.vendor || !receiptDraft.amount}
                className="w-full h-11 rounded-[8px] font-bold text-black text-sm disabled:opacity-50" style={{ background: GREEN }} data-testid="receipt-save">
                {busy ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
